import React from 'react';
import LegalLayout from '../components/layout/LegalLayout';

const C = { amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A', ink:'#1A1814', muted:'#7A7570', border:'#E8E4DC', green:'#16A34A', blue:'#2563EB', purple:'#7C3AED' };

type Tag = 'New'|'New Feature'|'Improved'|'Fixed'|'Removed';
const badge = (tag: Tag) => {
  const map: Record<Tag,{bg:string;color:string}> = {
    New:         { bg:'#F0FDF4', color:C.green },
    'New Feature': { bg:'#F0FDF4', color:C.green },
    Improved:    { bg:C.amber100, color:C.amber500 },
    Fixed:       { bg:'#EFF6FF', color:C.blue },
    Removed:     { bg:'#FEF2F2', color:'#DC2626' },
  };
  const s = map[tag];
  return <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, background:s.bg, color:s.color, fontFamily:"'DM Sans',sans-serif", flexShrink:0 }}>{tag}</span>;
};

const entry = (tag: Tag, text: string) => (
  <div key={text} style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:12 }}>
    {badge(tag)}
    <span style={{ fontSize:14, color:'#3D3A34', fontFamily:"'DM Sans',sans-serif", lineHeight:1.6 }}>{text}</span>
  </div>
);

const release = (version: string, date: string, summary: string, entries: React.ReactNode[]) => (
  <div key={version} style={{ display:'grid', gridTemplateColumns:'200px 1fr', gap:'0 40px', marginBottom:52, alignItems:'start' }}>
    <div style={{ position:'sticky', top:80 }}>
      <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 12px', borderRadius:20, background:C.amber100, border:`1px solid ${C.amber200}`, marginBottom:8 }}>
        <span style={{ fontSize:13, fontWeight:700, color:C.amber500, fontFamily:"'Sora',sans-serif" }}>v{version}</span>
      </div>
      <p style={{ fontSize:12, color:C.muted, fontFamily:"'DM Sans',sans-serif", margin:'0 0 4px' }}>{date}</p>
    </div>
    <div>
      <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:700, fontSize:'clamp(16px,2vw,20px)', color:C.ink, marginBottom:8, marginTop:0 }}>{summary}</h2>
      <div style={{ background:'#FFFFFF', border:`1px solid ${C.border}`, borderRadius:14, padding:'20px 24px' }}>
        {entries}
      </div>
    </div>
    <style>{`@media(max-width:640px){.changelog-grid{grid-template-columns:1fr!important;}}`}</style>
  </div>
);

const ChangelogPage: React.FC = () => (
  <LegalLayout
    title="Changelog"
    subtitle="Every update to Jötr, documented. We ship improvements every week."
  >
    {release('1.1.0','May 2026','Tasks — Your to-do list, built into Jötr',[
      entry('New Feature','Create tasks with title, description, due date and time'),
      entry('New Feature','Three priority levels — High, Medium and Low'),
      entry('New Feature','Browser notifications at exact due time'),
      entry('New Feature','Automatic overdue detection with red indicators'),
      entry('New Feature','Reschedule incomplete tasks to any future date'),
      entry('New Feature','Full task history — completed tasks saved forever'),
      entry('New Feature','Tasks appear in global search results'),
      entry('New Feature','Collapsible completed tasks section'),
    ])}
    {release('1.0.3','March 22, 2026','Search and editor improvements',[
      entry('Improved','Full-text search now highlights matched keywords in bold within the note preview.'),
      entry('Improved','The rich text editor toolbar is now sticky — stays visible as you scroll long notes.'),
      entry('Fixed','Tags with spaces were not being saved correctly. Now works as expected.'),
      entry('Fixed','Dark mode toggle in the top bar was not persisting across page refreshes.'),
      entry('New','Added word count to the note editor footer.'),
    ])}
    {release('1.0.2','March 15, 2026','Mobile and performance',[
      entry('New','Mobile sidebar drawer — tap the menu icon to slide in the folder tree on small screens.'),
      entry('Improved','Notes load 40% faster due to optimised PostgreSQL queries and client-side caching.'),
      entry('Improved','The onboarding flow now correctly skips the folder creation step if the user taps "skip".'),
      entry('Fixed','Auto-save was not triggering on mobile keyboards. Fixed by switching from keyup to input event.'),
    ])}
    {release('1.0.1','March 8, 2026','Bug fixes',[
      entry('Fixed','Login page was showing a blank screen on Safari due to an @import inside a style tag.'),
      entry('Fixed','Zustand store was causing infinite re-renders when using inline object selectors.'),
      entry('Fixed','Missing npm packages (@tiptap/extension-highlight) caused compile errors on fresh installs.'),
      entry('Improved','Error boundary now shows a readable error screen instead of a blank white page.'),
    ])}
    {release('1.0.0','March 1, 2026','Initial launch 🎉',[
      entry('New','Full-text search across all notes with real-time results and keyword highlighting.'),
      entry('New','Rich text editor powered by TipTap — bold, italic, headings, lists, code blocks.'),
      entry('New','Folders and sub-folders with custom colours.'),
      entry('New','Tags — assign multiple tags per note and filter by tag in the sidebar.'),
      entry('New','Auto-save — notes save automatically 800ms after you stop typing.'),
      entry('New','Cloud sync — notes available across all your devices.'),
      entry('New','Dark mode — system preference detected, manual toggle available.'),
      entry('New','PWA support — install Jötr on Android home screen.'),
    ])}
  </LegalLayout>
);

export default ChangelogPage;
